import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type {
  WaTemplate,
  WaTemplateButton,
  WaTemplateButtonType,
  WaTemplateCategory,
  WaTemplateLanguage,
  WaTemplateStatus,
} from '@/data/whatsappTemplateMocks'

/**
 * Templates reais da Meta Cloud API.
 *
 * Substitui o MOCK_TEMPLATES: a lista vem de account_wa_templates, a criação vai
 * para a Meta pela edge function meta-templates, e o status é o status real —
 * atualizado pelo webhook message_template_status_update ou pelo botão de
 * sincronizar.
 */

interface WaTemplateRow {
  id: string
  meta_template_id: string | null
  name: string
  language: string
  category: string
  status: string
  parameter_format: string
  components: unknown
  rejected_reason: string | null
  created_at: string
}

export interface CreateTemplateInput {
  name: string
  category: WaTemplateCategory
  language: WaTemplateLanguage
  headerText?: string
  body: string
  footer?: string
  buttons: WaTemplateButton[]
  bodyExamples: string[]
}

interface MetaComponent {
  type?: string
  format?: string
  text?: string
  buttons?: {
    type?: string
    text?: string
    url?: string
    phone_number?: string
  }[]
}

/** Converte os components da Meta para o formato que a UI já sabe renderizar. */
function rowToTemplate(row: WaTemplateRow): WaTemplate {
  const components: MetaComponent[] = Array.isArray(row.components)
    ? (row.components as MetaComponent[])
    : []

  const header = components.find((c) => c.type === 'HEADER')
  const body = components.find((c) => c.type === 'BODY')
  const footer = components.find((c) => c.type === 'FOOTER')
  const buttonsComponent = components.find((c) => c.type === 'BUTTONS')

  const headerFormat = (header?.format ?? 'TEXT').toUpperCase()
  const mediaType =
    headerFormat === 'IMAGE'
      ? 'image'
      : headerFormat === 'VIDEO'
        ? 'video'
        : headerFormat === 'DOCUMENT'
          ? 'document'
          : 'none'

  const buttons: WaTemplateButton[] = (buttonsComponent?.buttons ?? []).map((button, index) => ({
    id: `${row.id}_btn_${index}`,
    type: (button.type ?? 'QUICK_REPLY') as WaTemplateButtonType,
    text: button.text ?? '',
    url: button.url,
    phoneNumber: button.phone_number,
  }))

  return {
    id: row.id,
    name: row.name,
    category: row.category as WaTemplateCategory,
    language: row.language as WaTemplateLanguage,
    mediaType,
    headerText: headerFormat === 'TEXT' ? header?.text : undefined,
    body: body?.text ?? '',
    footer: footer?.text,
    buttons,
    status: row.status as WaTemplateStatus,
    createdAt: row.created_at,
  }
}

export function useWaTemplates() {
  const { user } = useAuth()
  const [templates, setTemplates] = useState<WaTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const accountId = user?.account_id

  const fetchLocal = useCallback(async () => {
    if (!accountId || !supabase) {
      setLoading(false)
      return
    }
    const { data, error: queryError } = await supabase
      .from('account_wa_templates')
      .select('*')
      .eq('account_id', accountId)
      .order('created_at', { ascending: false })

    if (queryError) {
      setError(queryError.message)
    } else {
      setTemplates(((data ?? []) as WaTemplateRow[]).map(rowToTemplate))
    }
    setLoading(false)
  }, [accountId])

  /** Busca a lista atual na Meta e reescreve o cache local. */
  const sync = useCallback(async (): Promise<boolean> => {
    if (!accountId) return false
    setSyncing(true)
    setError(null)
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/meta-templates?account_id=${accountId}`
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      })
      const result = await response.json()
      if (!result.success) {
        setError(result.error || 'Falha ao sincronizar com a Meta.')
        return false
      }
      setTemplates(((result.templates ?? []) as WaTemplateRow[]).map(rowToTemplate))
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao sincronizar com a Meta.')
      return false
    } finally {
      setSyncing(false)
    }
  }, [accountId])

  /** Cria o template na Meta e envia para análise. */
  const createTemplate = useCallback(
    async (input: CreateTemplateInput): Promise<{ ok: boolean; error?: string }> => {
      if (!accountId) return { ok: false, error: 'Conta não identificada.' }
      setError(null)
      try {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/meta-templates`
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ account_id: accountId, ...input }),
        })
        const result = await response.json()

        if (!result.success) {
          const message = result.error || 'Falha ao criar o template na Meta.'
          setError(message)
          return { ok: false, error: message }
        }

        await fetchLocal()
        return { ok: true }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro ao criar o template.'
        setError(message)
        return { ok: false, error: message }
      }
    },
    [accountId, fetchLocal]
  )

  useEffect(() => {
    if (!accountId || !supabase) return

    fetchLocal()

    // O status muda sozinho quando o webhook message_template_status_update
    // chega da Meta — sem precisar recarregar a página.
    // Nome único por instância: o hook é montado tanto na aba de Templates
    // quanto no seletor do chat, e canais homônimos colidiriam.
    const channel = supabase
      .channel(`wa-templates-${accountId}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_wa_templates',
          filter: `account_id=eq.${accountId}`,
        },
        () => {
          fetchLocal()
        }
      )
      .subscribe()

    return () => {
      if (supabase) supabase.removeChannel(channel)
    }
  }, [accountId, fetchLocal])

  return { templates, loading, syncing, error, sync, createTemplate, refetch: fetchLocal }
}
